import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

type MotionState = 'opening' | 'closing';

type UseDrawerDragOptions = {
  drawerRef: React.RefObject<HTMLDivElement | null>;
  peekAreaRef: React.RefObject<HTMLDivElement | null>;
  isMobile: boolean;
  onClose?: () => void;
  onPeekToggleAction?: (wasOpen: boolean) => void;
};

type UseDrawerDragResult = {
  open: boolean;
  motionState: MotionState;
  style: React.CSSProperties;
  peekAreaHandlers: React.HTMLAttributes<HTMLDivElement>;
  peekHandleHandlers: React.ButtonHTMLAttributes<HTMLButtonElement>;
  contentHandlers: React.HTMLAttributes<HTMLDivElement>;
  closeDrawer: () => void;
};

export function useDrawerDrag({
  drawerRef,
  peekAreaRef,
  isMobile,
  onClose,
  onPeekToggleAction,
}: UseDrawerDragOptions): UseDrawerDragResult {
  const [open, setOpen] = useState(false);
  const [dragStartYPos, setDragStartYPos] = useState<number | undefined>(undefined);
  const [translateYPercent, setTranslateYPercent] = useState(100);
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const [motionState, setMotionState] = useState<MotionState>('closing');
  const dragThreshold = 10;

  const getPeekHeight = () => peekAreaRef.current?.offsetHeight ?? 80;

  useEffect(() => {
    const el = drawerRef.current;
    if (!el || dragStartYPos === undefined) return;

    const onTouchMove = (e: TouchEvent) => {
      if (dragStartYPos === undefined || !drawerRef.current) return;
      e.preventDefault();

      const drawerHeight = drawerRef.current.offsetHeight;
      const deltaY = e.touches[0].clientY - dragStartYPos;
      const peekPercent = (getPeekHeight() / drawerHeight) * 100;
      const closedPercent = 100 - peekPercent;
      const basePercent = open ? 0 : closedPercent;
      const newPercent = (deltaY / drawerHeight) * 100 + basePercent;

      setTranslateYPercent(Math.min(Math.max(0, newPercent), closedPercent));
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [dragStartYPos, open]);

  const animateDrawer = useCallback((shouldOpen: boolean) => {
    if (!drawerRef.current) return;

    const drawerHeight = drawerRef.current.offsetHeight;
    const peekPercent = (getPeekHeight() / drawerHeight) * 100;

    setTransitionEnabled(true);
    setTranslateYPercent(shouldOpen ? 0 : 100 - peekPercent);
    setOpen(shouldOpen);

    setTimeout(() => setTransitionEnabled(false), 250);
    setDragStartYPos(undefined);
  }, []);

  useEffect(() => {
    if (!drawerRef.current) return;
    animateDrawer(true);
    setMotionState('opening');
  }, [animateDrawer]);

  useEffect(() => {
    if (!isMobile) {
      setTranslateYPercent(0);
      setOpen(true);
      setMotionState('opening');
    }
  }, [isMobile]);

  useEffect(() => {
    if (!open || !isMobile) return;
    const handleClickOutside = (e: PointerEvent) => {
      if (drawerRef.current && !e.composedPath().includes(drawerRef.current)) {
        animateDrawer(false);
        setMotionState('closing');
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, [open, isMobile, animateDrawer]);

  const releaseDrawer = (clientY: number) => {
    if (!drawerRef.current || dragStartYPos === undefined) return;

    const delta = clientY - dragStartYPos;
    if (Math.abs(delta) < dragThreshold) {
      setDragStartYPos(undefined);
      setMotionState(open ? 'opening' : 'closing');
      return;
    }

    const drawerHeight = drawerRef.current.offsetHeight;
    const hiddenPercent = (getPeekHeight() / drawerHeight) * 100;
    const shouldOpen = translateYPercent < hiddenPercent / 2;

    animateDrawer(shouldOpen);
    setMotionState(shouldOpen ? 'opening' : 'closing');

    if (!shouldOpen && !open) {
      onClose?.();
    }
  };

  const toggleDrawerPeek = () => {
    if (!drawerRef.current) return;
    animateDrawer(!open);
  };

  const style: React.CSSProperties = {
    transform: `translateY(${translateYPercent}%)`,
    transition: transitionEnabled ? 'transform 0.25s ease' : 'none',
  };

  const peekAreaHandlers: React.HTMLAttributes<HTMLDivElement> = isMobile
    ? {
        onPointerUp: (e) => {
          if (dragStartYPos !== undefined && Math.abs(e.clientY - dragStartYPos) >= dragThreshold) {
            releaseDrawer(e.clientY);
            return;
          }
          toggleDrawerPeek();
          onPeekToggleAction?.(open);
        },
        onPointerDown: (e) => {
          setDragStartYPos(e.clientY);
          setMotionState(open ? 'closing' : 'opening');
        },
      }
    : {};

  const peekHandleHandlers: React.ButtonHTMLAttributes<HTMLButtonElement> = isMobile
    ? {
        onPointerDown: (event) => event.stopPropagation(),
        onPointerUp: (event) => event.stopPropagation(),
        onClick: (event) => {
          event.stopPropagation();
          toggleDrawerPeek();
          onPeekToggleAction?.(open);
          setMotionState(open ? 'closing' : 'opening');
        },
      }
    : {};

  const contentHandlers: React.HTMLAttributes<HTMLDivElement> = isMobile
    ? {
        onTouchStart: (e) => {
          setMotionState(open ? 'closing' : 'opening');
          setDragStartYPos(e.touches[0].clientY);
        },
        onTouchEnd: (e) => releaseDrawer(e.changedTouches[0].clientY),
      }
    : {};

  const closeDrawer = useCallback(() => {
    animateDrawer(false);
    setMotionState('closing');
  }, [animateDrawer]);

  return { open, motionState, style, peekAreaHandlers, peekHandleHandlers, contentHandlers, closeDrawer };
}
